import { NEWS_QUANTITY_MAX, NEWS_QUANTITY_MIN, TipoSecaoHome } from '../../../../model/apm/apm-enums';
import { FieldErrors, FieldMap, validateFields } from '../../components/field-config';

export type HomeSectionField = 'title' | 'subtitle' | 'content' | 'itemsQuantity';
export type HomeItemField = 'image' | 'icon' | 'title' | 'description' | 'url' | 'openInNewTab' | 'period';

export interface HomeSectionTypeConfig {
    description: string;
    fields: FieldMap<HomeSectionField>;
    itemNoun?: string;
    itemFields?: FieldMap<HomeItemField>;
}

const title = { label: 'Título', kind: 'text' as const };
const subtitle = { label: 'Subtítulo', kind: 'text' as const };
const link = { label: 'Link', kind: 'url' as const };
const newTab = { label: 'Abrir em nova aba', kind: 'checkbox' as const };

export const HOME_SECTION_CONFIG: Record<TipoSecaoHome, HomeSectionTypeConfig> = {
    [TipoSecaoHome.Banners]: {
        description: 'Banners de corpo da home, exibidos na posição escolhida entre as demais seções. O carrossel do topo é cadastrado na tela de Banners.',
        fields: {},
        itemNoun: 'Banner',
        itemFields: {
            image: { label: 'Imagem', kind: 'image', required: true, requiredMessage: 'Imagem é obrigatória' },
            title: { label: 'Título (texto alternativo)', kind: 'text' },
            url: link,
            openInNewTab: newTab,
            period: { label: 'Período de exibição', kind: 'date' },
        },
    },
    [TipoSecaoHome.DiarioOficial]: {
        description: 'Últimas edições do Diário Oficial. Apenas posição e visibilidade são configuráveis.',
        fields: {},
    },
    [TipoSecaoHome.Noticias]: {
        description: 'Últimas notícias publicadas no portal.',
        fields: {
            itemsQuantity: {
                label: 'Quantidade de notícias', kind: 'number', required: true,
                min: NEWS_QUANTITY_MIN, max: NEWS_QUANTITY_MAX,
                requiredMessage: `A quantidade de notícias deve estar entre ${NEWS_QUANTITY_MIN} e ${NEWS_QUANTITY_MAX}.`,
                rangeMessage: `A quantidade de notícias deve estar entre ${NEWS_QUANTITY_MIN} e ${NEWS_QUANTITY_MAX}.`,
            },
        },
    },
    [TipoSecaoHome.TextoLivre]: {
        description: 'Bloco de texto formatado.',
        fields: { title, subtitle, content: { label: 'Conteúdo', kind: 'richText', required: true } },
    },
    [TipoSecaoHome.AcessoRapido]: {
        description: 'Atalhos com ícone para serviços e páginas.',
        fields: { title, subtitle },
        itemNoun: 'Atalho',
        itemFields: {
            icon: { label: 'Ícone', kind: 'icon', required: true },
            title: { label: 'Título', kind: 'text', required: true },
            description: { label: 'Descrição', kind: 'text', helper: 'Texto curto exibido abaixo do título (opcional).' },
            url: { ...link, required: true },
            openInNewTab: newTab,
        },
    },
    [TipoSecaoHome.CardDestaque]: {
        description: 'Cards com imagem, título e descrição.',
        fields: { title, subtitle },
        itemNoun: 'Card',
        itemFields: {
            image: { label: 'Imagem', kind: 'image', required: true, requiredMessage: 'Imagem é obrigatória' },
            title: { label: 'Título', kind: 'text', required: true },
            description: { label: 'Descrição', kind: 'multiline' },
            url: link,
            openInNewTab: newTab,
        },
    },
    [TipoSecaoHome.Videos]: {
        description: 'Vídeos do YouTube em destaque.',
        fields: { title, subtitle },
        itemNoun: 'Vídeo',
        itemFields: {
            title: { label: 'Título', kind: 'text' },
            url: { label: 'URL do vídeo (YouTube)', kind: 'youtube', required: true, requiredMessage: 'URL do vídeo é obrigatória' },
        },
    },
    [TipoSecaoHome.GaleriaImagens]: {
        description: 'Galeria de fotos em grade ou carrossel, com legenda opcional.',
        fields: { title, subtitle },
        itemNoun: 'Imagem',
        itemFields: {
            image: { label: 'Imagem', kind: 'image', required: true, requiredMessage: 'Imagem é obrigatória' },
            title: { label: 'Legenda', kind: 'text' },
            url: { ...link, helper: 'Sem link, a imagem é ampliada ao ser clicada no portal.' },
            openInNewTab: newTab,
        },
    },
};

// Layout da Galeria de Imagens, gravado como JSON em configuration e interpretado pelo portal público
export type GalleryLayout = 'grid' | 'carousel';

export interface GalleryConfiguration {
    layout: GalleryLayout;
    columns: number;
}

export const GALLERY_COLUMNS_MIN = 2;
export const GALLERY_COLUMNS_MAX = 6;
export const GALLERY_DEFAULT_CONFIGURATION: GalleryConfiguration = { layout: 'grid', columns: 3 };

// Mesmas regras do portal: JSON inválido cai no padrão e as colunas ficam entre 2 e 6
export function parseGalleryConfiguration(json?: string): GalleryConfiguration {
    let config: Partial<GalleryConfiguration> = {};
    try {
        config = json ? JSON.parse(json) : {};
    } catch {
        config = {};
    }
    const columns = Math.min(GALLERY_COLUMNS_MAX, Math.max(GALLERY_COLUMNS_MIN, Math.round(Number(config?.columns) || GALLERY_DEFAULT_CONFIGURATION.columns)));
    return { layout: config?.layout === 'carousel' ? 'carousel' : 'grid', columns };
}

export const serializeGalleryConfiguration = (config: GalleryConfiguration) => JSON.stringify(config);

export const homeSectionHasItems = (type: TipoSecaoHome) => !!HOME_SECTION_CONFIG[type]?.itemFields;

export function validateHomeItem(type: TipoSecaoHome, value: { startDate?: string; endDate?: string; [key: string]: any }): FieldErrors {
    const itemFields = HOME_SECTION_CONFIG[type]?.itemFields || {};
    const errors = validateFields(itemFields, value);
    if (itemFields.period && value.startDate && value.endDate && value.endDate < value.startDate) {
        errors.period = 'A data final de exibição deve ser maior ou igual à data inicial.';
    }
    return errors;
}
